import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  orderBy,
  query,
  where,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { getApp } from '@angular/fire/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { env } from '../env.generated';
import {
  SupportImageMeta,
  SupportMessage,
  SupportTicket,
  SupportTicketCategory,
  SupportTicketPriority,
} from '../models/support-ticket.model';

export interface CreateSupportTicketInput {
  churchId: string;
  subject: string;
  category: SupportTicketCategory;
  priority?: SupportTicketPriority;
  body: string;
  images?: File[];
}

export interface SendSupportMessageInput {
  ticketId: string;
  churchId: string;
  body: string;
  images?: File[];
  messageId?: string;
}

@Injectable({ providedIn: 'root' })
export class SupportService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);

  private getFunctionsInstance() {
    const region = env.NG_APP_FUNCTIONS_REGION || undefined;
    return region ? getFunctions(getApp(), region) : getFunctions(getApp());
  }

  /** IDs generados en cliente para poder subir imágenes antes de llamar a la CF. */
  newTicketIds(): { ticketId: string; messageId: string } {
    const ticketId = doc(collection(this.firestore, 'supportTickets')).id;
    const messageId = doc(
      collection(this.firestore, 'supportTickets', ticketId, 'messages')
    ).id;
    return { ticketId, messageId };
  }

  listenOpenTickets(churchId: string, uid?: string | null): Observable<SupportTicket[]> {
    if (!churchId) return of([]);

    return from(this.auth.authStateReady()).pipe(
      switchMap(() => {
        const ownerUid = uid || this.auth.currentUser?.uid;
        if (!ownerUid) return of([]);

        // Consulta owner-only (requiere índice compuesto).
        const ownerQuery = query(
          collection(this.firestore, 'supportTickets'),
          where('churchId', '==', churchId),
          where('createdByUid', '==', ownerUid),
          where('status', '==', 'open'),
          orderBy('updatedAt', 'desc')
        );

        return collectionData(ownerQuery, { idField: 'id' }).pipe(
          map((rows) => (rows as SupportTicket[]) || []),
          catchError((err) => {
            // Índice nuevo aún construyéndose, o reglas/consulta temporalmente incompatibles.
            console.warn('[support] owner query failed, using fallback', err?.code || err);
            const fallbackQuery = query(
              collection(this.firestore, 'supportTickets'),
              where('churchId', '==', churchId),
              where('status', '==', 'open'),
              orderBy('updatedAt', 'desc')
            );
            return collectionData(fallbackQuery, { idField: 'id' }).pipe(
              map((rows) =>
                ((rows as SupportTicket[]) || []).filter((t) => t.createdByUid === ownerUid)
              ),
              catchError((err2) => {
                console.error('[support] fallback query failed', err2?.code || err2);
                return of([] as SupportTicket[]);
              })
            );
          })
        );
      })
    );
  }

  listenMessages(ticketId: string): Observable<SupportMessage[]> {
    if (!ticketId) return of([]);
    const q = query(
      collection(this.firestore, 'supportTickets', ticketId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    return collectionData(q, { idField: 'id' }).pipe(
      map((rows) =>
        ((rows as SupportMessage[]) || []).map((m) => ({
          ...m,
          images: Array.isArray(m.images) ? m.images : [],
        }))
      )
    );
  }

  async uploadImages(
    churchId: string,
    ticketId: string,
    messageId: string,
    files: File[]
  ): Promise<SupportImageMeta[]> {
    if (!files?.length) return [];
    if (files.length > 3) {
      throw new Error('Máximo 3 imágenes por mensaje.');
    }

    const storage = getStorage();
    const uid = this.auth.currentUser?.uid || 'anon';
    const metas: SupportImageMeta[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
        throw new Error('Solo se permiten imágenes JPEG, PNG o WebP.');
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Cada imagen debe pesar máximo 5 MB.');
      }

      const ext =
        file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
      const fileName = `${Date.now()}_${uid}_${i}.${ext}`;
      const path = `support-tickets/${churchId}/${ticketId}/${messageId}/${fileName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadUrl = await getDownloadURL(storageRef);
      metas.push({
        path,
        downloadUrl,
        contentType: file.type,
        size: file.size,
        fileName: file.name || fileName,
      });
    }

    return metas;
  }

  async createTicket(input: CreateSupportTicketInput): Promise<{ ticketId: string }> {
    const { ticketId, messageId } = this.newTicketIds();
    const images = await this.uploadImages(
      input.churchId,
      ticketId,
      messageId,
      input.images || []
    );

    const fn = httpsCallable(this.getFunctionsInstance(), 'createSupportTicket');
    await fn({
      ticketId,
      messageId,
      churchId: input.churchId,
      subject: input.subject,
      category: input.category,
      priority: input.priority || 'media',
      body: input.body,
      images,
    });

    return { ticketId };
  }

  async sendMessage(input: SendSupportMessageInput): Promise<{ messageId: string }> {
    const messageId =
      input.messageId ||
      doc(collection(this.firestore, 'supportTickets', input.ticketId, 'messages')).id;
    const images = await this.uploadImages(
      input.churchId,
      input.ticketId,
      messageId,
      input.images || []
    );

    const fn = httpsCallable(this.getFunctionsInstance(), 'sendSupportMessage');
    await fn({
      ticketId: input.ticketId,
      messageId,
      body: input.body,
      images,
    });

    return { messageId };
  }

  /** Marca el ticket como leído en Firestore (y emite refresh local vía caller). */
  async markTicketRead(ticketId: string): Promise<void> {
    if (!ticketId) return;
    const fn = httpsCallable(this.getFunctionsInstance(), 'markSupportTicketRead');
    await fn({ ticketId });
  }

  async exitChurchAsSupport(): Promise<void> {
    const fn = httpsCallable(this.getFunctionsInstance(), 'exitChurchAsSupport');
    await fn({});
    await this.auth.currentUser?.getIdToken(true);
  }
}
